/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import { compose } from 'recompose';
import { connect, useSelector, useDispatch } from 'react-redux';
import {
    object, string, func,
} from 'prop-types';
import loadable from '@loadable/component';
import { useLocation } from 'react-router';
import { getSSRDeviceType } from '../../../../state/ducks/App/App-Selectors';
import { getFeatureFlag } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import useUIDQueryConditionally from '../../../helpers/hooks/useUIDQueryConditionally';
import GenericSkeleton from '../../GraphqlComponents/GraphqlSkeletonComponents/GenericSkeleton';
import { getCurrentPageType } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Selectors';
import { getBrandName } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import useSalesforcePersonalizedContentQuery from '../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import useClickStreamCustomEventsTracking from '../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import { trackEvent } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import HeroSingleUI from './HeroSingleUI';

const HeroSingleResponsive = loadable(() => import(/* webpackChunkName: "HeroSingleResponsive" */  './HeroSingleResponsive'));
const HeroStory = loadable(() => import(/* webpackChunkName: "HeroStory" */  '../HeroStory/HeroStory'));
const HeroDepartment = loadable(() => import(/* webpackChunkName: "HeroDepartment" */  '../HeroDepartment/HeroDepartment'));
const HeroSingleV2 = loadable(() => import(/* webpackChunkName: "HeroSingle_V2" */  '../HeroSingle_V2/Responsive/HeroSingle_V2'));

const Hero = ({
    data: blockData, ssrDeviceType, salesforceResponse, setShowSkeleton, blockKey,
}) => {
    const { data, loading } = useUIDQueryConditionally({
        data: blockData,
        includeRefs: ['reference.message_collection.link_list.reference'],
        newDataSelector: (dta) => dta?.findContentByUID?.content?.entry || data,
    });
    const location = useLocation();
    const ffHeroRedesign = useSelector(getFeatureFlag('is-department-page-hero-redesign-ab-test-enabled'));
    const brand = useSelector(getBrandName);
    const pageType = useSelector(getCurrentPageType);
    const dispatch = useDispatch();
    useEffect(() => {
        setShowSkeleton(false);
    }, []);
    const departmentHeroWhiteListFlag = useSelector(getFeatureFlag('which-dept-hero-ab-test-path-is-enabled'))?.includedPathList?.includes(location?.pathname);
    const { loading: heroRedesignLoading, variables, data: heroRedesignData } = useSalesforcePersonalizedContentQuery({
        queryName: 'departmentPageHeroRedesign',
        interactionName: `${brand} - Get Campaign - Department Page Hero Redesign`,
        featureFlag: ffHeroRedesign && departmentHeroWhiteListFlag,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
    });
    const isHeroRedesignEnabled = heroRedesignData?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;

    useClickStreamCustomEventsTracking({
        salesforceResponse: heroRedesignData?.contentSF,
        isFireImpression: true,
        page: { type: pageType },
    });

    useEffect(() => {
        if (typeof heroRedesignData !== 'undefined' && typeof window !== 'undefined' && !window.wuDepartmentPageHeroEventFired && heroRedesignData) {
            // GA tracking
            const eventParam = {
                eventCategory: 'Experiment',
                eventAction: 'Department Page Hero Redesign',
                eventLabel: isHeroRedesignEnabled?.toLowerCase() === 'test' ? 'Variant' : 'Control',
                feature_detail: isHeroRedesignEnabled?.toLowerCase() === 'test' ? 'Variant' : 'Control',
                feature_element: 'Department Page Hero Redesign',
                feature_category: 'Experiment',
            };
            dispatch(trackEvent(eventParam));
            window.wuDepartmentPageHeroEventFired = true;
        }
        return () => {
            window.wuDepartmentPageHeroEventFired = false;
        };
    }, [heroRedesignData]);

    if ((heroRedesignLoading || variables?.skip || loading)) {
        const mobileViewPort = ssrDeviceType.toLowerCase() === 'mobile';
        if (mobileViewPort && blockData?.mobile_skeleton) {
            return <GenericSkeleton height={blockData?.mobile_skeleton_height || blockData?.skeleton_height} />;
        }
        if (ssrDeviceType.toLowerCase() === 'desktop' && blockData?.desktop_skeleton) {
            return <GenericSkeleton height={blockData?.skeleton_height} />;
        }
        return (blockKey !== 1 && (heroRedesignLoading || variables?.skip)) && <GenericSkeleton height={mobileViewPort ? 400 : 600} maxWidth="100%" />;
    }

    let heroDepartmentPageRender = !(ffHeroRedesign && departmentHeroWhiteListFlag);
    if (typeof isHeroRedesignEnabled !== 'undefined' && isHeroRedesignEnabled) {
        const userGroup = isHeroRedesignEnabled?.toLowerCase();
        const abTestingGroup = data?.reference?.[0]?.a_b_testing?.variant?.toLowerCase();
        const isControlMatch = userGroup === 'control' && abTestingGroup === 'control';
        const isTestMatch = userGroup === 'test' && abTestingGroup === 'variant';
        heroDepartmentPageRender = isControlMatch || isTestMatch;
    }
    if (!data) return <></>;
    if (data?.reference?.[0]?.['_content_type_uid'] === 'herosingle_v2') return <HeroSingleV2 data={data} />;
    if (data?.reference?.[0]?.['_content_type_uid'] === 'hero_story') return <HeroStory data={data} />;
    if (data?.reference?.[0]?.['_content_type_uid'] === 'hero_single_responsive') { return <HeroSingleResponsive data={data?.reference?.[0]} />; }
    if (data?.reference?.[0]?.['_content_type_uid'] === 'department_hero') return <HeroDepartment data={data} />;

    let block = null;
    if (data && !data?.reference?.[0]?.reference?.[0]) {
        block = data;
    } else if (data || data?.reference?.[0]?.reference?.[0]) {
        block = data.reference[0];
    }

    if (!block || !block?.reference?.[0]) {
        return null;
    }

    return (
        heroDepartmentPageRender &&  (
            <HeroSingleUI
                salesforceResponse={salesforceResponse}
                block={block}
                data={data}
                isHeroRedesignEnabled={isHeroRedesignEnabled}
            />
        )
    );
};

Hero.propTypes = {
    data: object.isRequired,
    ssrDeviceType: string.isRequired,
    salesforceResponse: object,
    setShowSkeleton: func,
    blockKey: string,
};
Hero.defaultProps = {
    salesforceResponse: {},
    setShowSkeleton: () => {},
    blockKey: '',
};
const mapStateToProps = (state) => ({
    ssrDeviceType: getSSRDeviceType(state),
});

const enhance = compose(
    connect(mapStateToProps, null),
);
export default enhance(Hero);
